import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import {
    EntityBody,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
} from '@/Ship/Components/EntityCard'

import { UserAccessTab } from '../Components/Tabs/UserAccessTab'
import { UserGmailPasswordTab } from '../Components/Tabs/UserGmailPasswordTab'
import { UserHistoryTab } from '../Components/Tabs/UserHistoryTab'
import { UserPasswordTab } from '../Components/Tabs/UserPasswordTab'
import { UserCardHeader } from '../Components/UserCardHeader'
import { UserRoleSwitches } from '../Components/UserRoleSwitches'
import { UserStatusField } from '../Components/UserStatusField'

import type { UserCrudContextLike, UserFormContextLike, UserPermissions } from '../Contracts/IUser'
import type { ReactNode } from 'react'

interface Props {
    crudContext: UserCrudContextLike | undefined
    formContext: UserFormContextLike | undefined
}

/**
 * Блоки «Загального» — кожен відповідає на одне питання (`SPEC-005` §7.2):
 *
 *  - **Головне** — хто це: ПІБ, посада, відділи. Фото — лише у формі: у
 *    перегляді воно стоїть у шапці замість ініціалів;
 *  - **Контакти** — як зв'язатися: мобільний (він же логін), телефони, пошта;
 *  - **Доступ** — що людині дозволено: активність і остання активність; у
 *    формі створення — пароль і ролі (Q6: пароль задає супер адміністратор);
 *  - **Службова інформація** — коли запис створено й змінено.
 */
const SECTIONS = {
    main: ['lastName', 'firstName', 'middleName', 'position', 'departments', 'photo', 'sorting'],
    contacts: ['phone', 'phones', 'email'],
    access: ['lastActivityAt'],
    accessNew: ['plainPassword', 'plainPasswordConfirm'],
    service: ['createdAt', 'updatedAt'],
} as const

// На всю ширину: кілька відділів і телефонів у половині блоку ховалися б за
// лічильником «+1»
const WIDE = new Set<string>(['departments', 'phones', 'photo'])

/** Проставляє сервер — у формі нового співробітника їм нема чого показати. */
const SERVER_FIELDS = new Set<string>(['lastActivityAt', ...SECTIONS.service])

/** Фото — лише у формі; у перегляді ядро малювало б порожню зону завантаження. */
const PhotoCell = observer(({ formContext }: { formContext: UserFormContextLike | undefined }) =>
    formContext?.isEditable === true ? (
        <FieldCell wide>
            <FormField name="photo" />
        </FieldCell>
    ) : null
)

/**
 * Картка співробітника на спільних правилах карток (`EntityLayout`): шапка,
 * вкладки, блоки полів двома стовпцями.
 *
 * Вкладки — за правами картки (`_permissions`), а не за ролями (`SPEC-003`
 * §1.3): «Права доступу» — `update_access`, «Змінити пароль» — `update`,
 * «Пароль Google Workspace» — `update_gmail_app_password`, «Історія змін» —
 * кожному, хто читає. У формі створення вкладок немає: пароль і ролі стоять
 * у блоці «Доступ».
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid` і права
 * (`SPEC-002` §4.2): інакше ядровий `FormField` перемонтовував би всі поля на
 * кожну літеру. Живі значення показують окремі observer-компоненти
 * (`UserCardHeader`, `UserStatusField`, `UserRoleSwitches`).
 */
export const UserEditTemplate = observer(({ crudContext, formContext }: Props) => {
    // Лічильник відкриттів «Історії змін»: antd тримає вкладку змонтованою, і
    // без перемонтування вона не побачила б щойно змінених ролей чи статусу
    const [historyEpoch, setHistoryEpoch] = useState(0)

    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    const permissions: UserPermissions = formContext?.values?._permissions ?? {}

    const cell = (name: string): ReactNode => {
        if (!(name in fields) || (isNew && SERVER_FIELDS.has(name))) return null

        if (name === 'photo') return <PhotoCell key={name} formContext={formContext} />

        return (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )
    }

    const grid = (names: readonly string[], extra?: ReactNode) => (
        <Form formContext={formContext}>
            <FieldGrid>
                {extra}
                {names.map(cell)}
            </FieldGrid>
        </Form>
    )

    const main = (
        <>
            <FieldSection title="Головне">{grid(SECTIONS.main)}</FieldSection>
            <FieldSection title="Контакти">{grid(SECTIONS.contacts)}</FieldSection>
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                <EntityBody
                    main={main}
                    aside={
                        <FieldSection title="Доступ">
                            {grid(SECTIONS.accessNew)}
                            {formContext ? <UserRoleSwitches formContext={formContext} /> : null}
                        </FieldSection>
                    }
                />
            </EntityLayout>
        )
    }

    const aside = (
        <>
            <FieldSection title="Доступ">
                {grid(
                    SECTIONS.access,
                    <FieldCell wide>
                        <UserStatusField formContext={formContext} crudContext={crudContext} />
                    </FieldCell>
                )}
            </FieldSection>
            <FieldSection title="Службова інформація">{grid(SECTIONS.service)}</FieldSection>
        </>
    )

    const tabs = [
        { key: 'general', label: 'Загальне', children: <EntityBody main={main} aside={aside} /> },
        ...(permissions.update_access === true
            ? [{ key: 'access', label: 'Права доступу', children: <UserAccessTab formContext={formContext} /> }]
            : []),
        ...(permissions.update === true
            ? [{ key: 'password', label: 'Змінити пароль', children: <UserPasswordTab formContext={formContext} /> }]
            : []),
        ...(permissions.update_gmail_app_password === true
            ? [
                  {
                      key: 'gmail',
                      label: 'Пароль Google Workspace',
                      children: <UserGmailPasswordTab formContext={formContext} />,
                  },
              ]
            : []),
        {
            key: 'history',
            label: 'Історія змін',
            children: (
                <EntityPanel>
                    <UserHistoryTab key={historyEpoch} formContext={formContext} />
                </EntityPanel>
            ),
        },
    ]

    return (
        <EntityLayout>
            <EntityTabs
                header={<UserCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'history') setHistoryEpoch((epoch) => epoch + 1)
                }}
                items={tabs}
            />
        </EntityLayout>
    )
})
